import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { translate } from '@utils/i18n';
import './Visits.scss';
import { Accordion } from '@ui/molecules/Accordion';
import Table, { TableSize } from '@components/Table';
import { TimeZoneContextProvider } from '@contexts/timezone-context';
import { TenantContextProvider } from '@contexts/tenant-context';
import EasyUploadLinkModal from '@features/Modals/EasyUploadLinkModal';
import getVisitsColumns from './visitsColumns';
import NewVisitModal from './NewVisitModal';
import EditVisitModal from './EditVisitModal';

const namespace = 'features.work_orders.visits_section';

function VisitsSection({
  title,
  visits: initialVisits,
  workOrderId,
  subcontractors,
  technicians,
  priorities,
  timeZones,
  visitStatuses,
  defaultTechnicianId,
  defaultSubcontractorId,
  defaultAssigneeType,
  buildingId,
  defaultVisitStatus,
  isSubcontractorsEnabled,
  visitDefaultTimeZone: initialVisitDefaultTimeZone,
  defaultTimeZone,
  timeZonesMapping
}) {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [visitEditing, setVisitEditing] = useState(null);
  const [subcontractWorkStatusId, setSubcontractWorkStatusId] = useState(null);
  const [visits, setVisits] = useState(initialVisits);
  const [visitDefaultTimeZone, setVisitDefaultTimeZone] = useState(initialVisitDefaultTimeZone);

  const onRequestEdit = (visitId) => {
    const selectedVisit = visits.find((visit) => visit.id === visitId);
    setVisitEditing(selectedVisit);
  };

  const handleEasyUploadModalOpen = (visitId) => {
    const selectedVisit = visits.find((visit) => visit.id === visitId);

    if (!selectedVisit) return;

    setSubcontractWorkStatusId(selectedVisit.subcontract_work_status_id);
  };

  useEffect(() => {
    function handleOpenModal(event) {
      const selectedVisit = visits.find((visit) => visit.id === event.detail);
      setVisitEditing(selectedVisit);
    }

    window.addEventListener('openVisitModal', handleOpenModal);

    return () => {
      window.removeEventListener('openVisitModal', handleOpenModal);
    };
  }, [visits]);

  useEffect(() => {
    if (visits.every((visit) => visit.id)) return;

    setVisits(visits.map((visit) => ({ ...visit, id: Math.random() })));
  }, []);

  useEffect(() => {
    if (workOrderId) return;

    const workOrderTimeZoneDropdown = document.getElementById('work_order_time_zone_override');

    if (workOrderTimeZoneDropdown) {
      const handleWorkOrderTimeZoneChange = (e) => {
        const workOrderTimeZone = e.target.value;

        setVisitDefaultTimeZone(workOrderTimeZone);
        setVisits((prevState) => prevState.map((visit) => ({ ...visit, time_zone: workOrderTimeZone })));
      };

      workOrderTimeZoneDropdown.addEventListener('change', handleWorkOrderTimeZoneChange);

      return () => {
        workOrderTimeZoneDropdown.removeEventListener('change', handleWorkOrderTimeZoneChange);
      };
    }
  }, []);

  const onRequestAdd = () => {
    setIsAddOpen(true);
  };

  const onAddComplete = (newVisitInfo) => {
    setVisits([newVisitInfo, ...visits]);
    setIsAddOpen(false);
  };

  const onEditComplete = (newVisitInfo) => {
    setVisits(visits.map((visit) => (visit.id === newVisitInfo.id ? { ...visit, ...newVisitInfo } : visit)));
    setVisitEditing(null);
  };

  const onDelete = (id) => {
    const index = visits.findIndex((visit) => visit.id === id);

    setVisits([...visits.slice(0, index), ...visits.slice(index + 1)]);
  };

  return (
    <TimeZoneContextProvider initialTimeZone={defaultTimeZone} timeZonesMapping={timeZonesMapping}>
      <TenantContextProvider>
        <React.Fragment key="index">
          <div className="span10 visitSection">
            <Accordion
              variant="section"
              defaultExpanded
              title={
                <span className="header" id="work-order-visits">
                  {title}
                </span>
              }
              actions={[
                {
                  icon: <i className="fa-light fa-circle-plus" />,
                  label: translate('add_visit', { namespace }),
                  buttonProps: {
                    id: 'add-visit-button',
                    variant: 'highlighted',
                    onClick: onRequestAdd
                  }
                }
              ]}>
              <div className="visitSectionContent">
                {visits.length > 0 && (
                  <Table
                    size={TableSize.Full}
                    data={visits}
                    columns={getVisitsColumns(
                      onRequestEdit,
                      onDelete,
                      visitStatuses,
                      subcontractors,
                      technicians,
                      workOrderId,
                      defaultTimeZone,
                      handleEasyUploadModalOpen
                    )}
                  />
                )}
              </div>
            </Accordion>
          </div>

          <NewVisitModal
            woId={workOrderId}
            vendorOptions={subcontractors}
            techOptions={technicians}
            priorityOptions={priorities}
            timeZoneOptions={timeZones}
            isOpen={isAddOpen}
            onClose={() => setIsAddOpen(false)}
            defaultTechnicianId={defaultTechnicianId}
            defaultSubcontractorId={defaultSubcontractorId}
            statusesOptions={visitStatuses}
            onAddComplete={onAddComplete}
            defaultAssigneeType={defaultAssigneeType}
            defaultVisitStatus={defaultVisitStatus}
            isSubcontractorsEnabled={isSubcontractorsEnabled}
            defaultTimeZone={visitDefaultTimeZone}
          />
          <EditVisitModal
            woId={workOrderId}
            vendorOptions={subcontractors}
            techOptions={technicians}
            priorityOptions={priorities}
            timeZoneOptions={timeZones}
            visit={visitEditing}
            isOpen={!!visitEditing}
            onClose={() => setVisitEditing(null)}
            statusesOptions={visitStatuses}
            onEditComplete={onEditComplete}
            defaultTechnicianId={defaultTechnicianId}
            defaultSubcontractorId={defaultSubcontractorId}
            isSubcontractorsEnabled={isSubcontractorsEnabled}
          />
          <EasyUploadLinkModal
            subcontractWorkStatusId={subcontractWorkStatusId}
            onClose={() => setSubcontractWorkStatusId(null)}
          />

          {!workOrderId &&
            visits.map((visit, index) => (
              <React.Fragment key={index}>
                <input type="hidden" name={`work_order[visits_attributes][${index}][building_id]`} value={buildingId} />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][time_zone_override]`}
                  value={visit.time_zone}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][scheduled_date]`}
                  value={new Intl.DateTimeFormat('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    timeZone: timeZonesMapping[visit.time_zone]
                  }).format(new Date(visit.start))}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][scheduled_time]`}
                  value={new Intl.DateTimeFormat('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: timeZonesMapping[visit.time_zone]
                  }).format(new Date(visit.start))}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][duration_days]`}
                  value={Math.floor(visit.duration / (24 * 60))}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][duration_hours]`}
                  value={Math.floor((visit.duration % (24 * 60)) / 60)}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][duration_minutes]`}
                  value={visit.duration % 60}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][technician_id]`}
                  value={visit.technician_id}
                />
                <input type="hidden" name={`work_order[visits_attributes][${index}][notes]`} value={visit.notes} />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][visit_status]`}
                  value={visit.visit_status}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][subcontractor_id]`}
                  value={visit.subcontractor_id}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][subcontractor_po_number]`}
                  value={visit.subcontractor_po_number}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][visit_notes]`}
                  value={visit.visit_notes}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][work_date]`}
                  value={
                    visit.work_date
                      ? new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          timeZone: timeZonesMapping[visit.time_zone]
                        }).format(new Date(visit.work_date))
                      : ''
                  }
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][work_performed]`}
                  value={visit.work_performed}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][follow_up]`}
                  value={visit.follow_up}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][subcontractor_billing_limit]`}
                  value={visit.subcontractor_billing_limit}
                />
                <input
                  type="hidden"
                  name={`work_order[visits_attributes][${index}][assignee_type]`}
                  value={visit.assignee_type}
                />
              </React.Fragment>
            ))}
        </React.Fragment>
      </TenantContextProvider>
    </TimeZoneContextProvider>
  );
}

VisitsSection.propTypes = {
  title: PropTypes.string,
  visits: PropTypes.array,
  workOrderId: PropTypes.number,
  technicians: PropTypes.array,
  priorities: PropTypes.array,
  timeZones: PropTypes.array,
  defaultTechnicianId: PropTypes.number,
  defaultSubcontractorId: PropTypes.number,
  defaultAssigneeType: PropTypes.string.isRequired,
  buildingId: PropTypes.number.isRequired,
  subcontractors: PropTypes.object,
  visitStatuses: PropTypes.array,
  defaultVisitStatus: PropTypes.string.isRequired,
  isSubcontractorsEnabled: PropTypes.bool.isRequired,
  visitDefaultTimeZone: PropTypes.string.isRequired,
  defaultTimeZone: PropTypes.string.isRequired,
  timeZonesMapping: PropTypes.object.isRequired
};

VisitsSection.defaultProps = {
  title: '',
  visits: [],
  technicians: [],
  priorities: [],
  timeZones: [],
  defaultTechnicianId: null,
  workOrderId: null,
  defaultSubcontractorId: null,
  subcontractors: {},
  visitStatuses: []
};

export default VisitsSection;
